'use client';

// eslint-disable-next-line import/no-unresolved
import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import queryString from 'query-string';
import isClient from 'commons/isClient';
import { buildCategoryPageUrlParams, parseCategoryPageUrlState } from '../categoryPageUrlState';
const getCurrentQueryString = () => isClient() ? window.location.search.replace(/^\?/, '') : '';
const buildPathWithQuery = (pathname, queryStringValue) => {
  const basePath = `${pathname}${pathname.endsWith('/') ? '' : '/'}`;
  return queryStringValue ? `${basePath}?${queryStringValue}` : basePath;
};
const parsePagesParam = pagesParam => {
  const parsedPages = parseInt(pagesParam ?? '0', 10);
  return Number.isFinite(parsedPages) && parsedPages >= 0 ? parsedPages : 0;
};
const getPagesFromSearch = search => {
  const query = search.startsWith('?') ? search.slice(1) : search;
  return parsePagesParam(new URLSearchParams(query).get('pages'));
};

/**
 * Manages URL params for Algolia category pages.
 *
 * Filter/sort updates use `history.replaceState` (not `router.replace`) so
 * changing filters does not trigger a Next.js RSC refetch on every toggle.
 * Reload and back/forward still work because the browser URL is updated.
 */
const useCategoryPageURL = (slug, t0) => {
  const $ = _c(31);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const filters = t1;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t2;
  if ($[2] !== searchParams) {
    t2 = () => {
      if (isClient()) {
        return getPagesFromSearch(window.location.search);
      }
      return parsePagesParam(searchParams?.get("pages"));
    };
    $[2] = searchParams;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [pagesUrlParam, setPagesUrlParam] = useState(t2);
  let t3;
  if ($[4] !== filters || $[5] !== searchParams) {
    t3 = parseCategoryPageUrlState(searchParams, filters);
    $[4] = filters;
    $[5] = searchParams;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const parsedUrlState = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = nextUrl => {
      if (!isClient()) {
        return;
      }
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      if (nextUrl === currentUrl) {
        return;
      }
      window.history.replaceState(window.history.state, "", nextUrl);
      const queryStart = nextUrl.indexOf("?");
      const query = queryStart >= 0 ? nextUrl.slice(queryStart) : "";
      setPagesUrlParam(getPagesFromSearch(query));
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const replaceUrlQuery = t4;
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (!isClient()) {
        return;
      }
      const syncPagesFromLocation = () => {
        setPagesUrlParam(getPagesFromSearch(window.location.search));
      };
      window.addEventListener("popstate", syncPagesFromLocation);
      return () => window.removeEventListener("popstate", syncPagesFromLocation);
    };
    t6 = [];
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (!isClient()) {
        return;
      }
      setPagesUrlParam(getPagesFromSearch(window.location.search));
    };
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== searchParams) {
    t8 = [searchParams];
    $[11] = searchParams;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  useEffect(t7, t8);
  const prevSlugRef = useRef(slug);
  let t10;
  let t9;
  if ($[13] !== filters || $[14] !== pathname || $[15] !== slug) {
    t9 = () => {
      if (prevSlugRef.current === slug || !isClient()) {
        return;
      }
      prevSlugRef.current = slug;
      const currentParams = queryString.parse(window.location.search);
      const filterKeys = new Set(["pages", "sortBy", "quickship", "widthRange", "search", "color", ...(filters || []).map(_temp)]);
      const clearedParams = Object.fromEntries(Object.entries(currentParams).filter(t11 => {
        const [key] = t11;
        return !filterKeys.has(key);
      }));
      replaceUrlQuery(buildPathWithQuery(pathname, queryString.stringify(clearedParams)));
    };
    t10 = [slug, pathname, filters, replaceUrlQuery];
    $[13] = filters;
    $[14] = pathname;
    $[15] = slug;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] !== pathname) {
    t11 = nextParams => {
      const nextQuery = queryString.stringify(nextParams);
      const currentQuery = getCurrentQueryString();
      if (nextQuery === currentQuery) {
        return;
      }
      replaceUrlQuery(buildPathWithQuery(pathname, nextQuery));
    };
    $[18] = pathname;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const replaceUrlParams = t11;
  let t12;
  if ($[20] !== replaceUrlParams) {
    t12 = updates => {
      if (!updates) {
        return;
      }
      const prev = queryString.parse(getCurrentQueryString());
      const merged = {
        ...prev,
        ...updates
      };
      Object.keys(merged).forEach(key_0 => {
        if (merged[key_0] === undefined || merged[key_0] === null) {
          delete merged[key_0];
        }
      });
      replaceUrlParams(merged);
    };
    $[20] = replaceUrlParams;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const updateUrlParamImmediate = t12;
  let t13;
  if ($[22] !== filters || $[23] !== pagesUrlParam || $[24] !== replaceUrlParams) {
    t13 = (filterState, t14) => {
      const {
        resetPages: t15
      } = t14 === undefined ? {} : t14;
      const resetPages = t15 === undefined ? false : t15;
      const existingParams = queryString.parse(getCurrentQueryString());
      const nextParams_0 = buildCategoryPageUrlParams({
        ...filterState,
        filters,
        pages: resetPages ? 0 : pagesUrlParam,
        existingParams
      });
      if (resetPages) {
        delete nextParams_0.pages;
      }
      replaceUrlParams(nextParams_0);
    };
    $[22] = filters;
    $[23] = pagesUrlParam;
    $[24] = replaceUrlParams;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const syncFilterStateToUrl = t13;
  let t14;
  if ($[26] !== pagesUrlParam || $[27] !== parsedUrlState || $[28] !== syncFilterStateToUrl || $[29] !== updateUrlParamImmediate) {
    t14 = {
      pagesUrlParam,
      parsedUrlState,
      updateUrlParamImmediate,
      syncFilterStateToUrl
    };
    $[26] = pagesUrlParam;
    $[27] = parsedUrlState;
    $[28] = syncFilterStateToUrl;
    $[29] = updateUrlParamImmediate;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
export default useCategoryPageURL;
function _temp(t0) {
  const {
    id
  } = t0;
  return id;
}